import axios, { type AxiosResponse } from 'axios'
import { mutate } from 'swr'
import type {
  IImpersonationOriginalUser,
  ISession,
  ISessionDetailed,
} from '@/modules/_misc/Session'
import type { IEntitySimplified } from '@/modules/Entity/Entity'
import type { IUser } from '@/modules/User/User'

//  //////////////////////////////////////////////
//  MARK: SESSION EXPIRATION HELPERS

// Fenêtre de vie estimée d'un refresh_token côté serveur. spring-security-rest
// utilise par défaut 30 jours, Keycloak typiquement 14 jours pour un refresh
// non-offline. On prend 30 jours comme borne haute : si le serveur invalide
// avant, l'intercepteur axios déconnectera la session à la prochaine activité.
const REFRESH_TOKEN_WINDOW_MS = 30 * 24 * 3600 * 1000

const FALLBACK_ACCESS_EXPIRES_IN_S = 3600

function computeSessionExpiration(expiresInSeconds: number | undefined): {
  access_expires_at: number
  refresh_expires_at: number
} {
  const now = Date.now()
  const expiresIn =
    typeof expiresInSeconds === 'number' && expiresInSeconds > 0
      ? expiresInSeconds
      : FALLBACK_ACCESS_EXPIRES_IN_S

  return {
    access_expires_at: now + expiresIn * 1000,
    refresh_expires_at: now + REFRESH_TOKEN_WINDOW_MS,
  }
}

/**
 * Une session est considérée définitivement perdue (à purger du multi-session)
 * quand son access_token et son refresh_token sont tous les deux expirés.
 * Les sessions sans timestamp (legacy storage) sont conservées — l'intercepteur
 * axios les nettoiera naturellement sur la première 401/refresh raté.
 */
export function isSessionFullyExpired(
  session: ISessionDetailed,
  now: number = Date.now(),
): boolean {
  if (session.refresh_expires_at == null && session.access_expires_at == null) {
    return false
  }

  const accessExpired = session.access_expires_at != null && now >= session.access_expires_at
  const refreshExpired = session.refresh_expires_at != null && now >= session.refresh_expires_at

  return accessExpired && refreshExpired
}

async function finalizeImpersonationLogin(
  response: IImpersonateResponse,
  functions: IAppAuthFunctions,
): Promise<ISessionDetailed> {
  const profile = await axios.get<IUser>('/api/user/profile', {
    headers: {
      Authorization: `${response.token_type} ${response.access_token}`,
    },
  })

  mutate(['/api/user/profile', profile.data.username], profile.data, { revalidate: false })
  mutate([`/api/user/${profile.data.id}`, profile.data.username], profile.data, {
    revalidate: false,
  })
  mutate([`/api/person/${profile.data.id}`, profile.data.username], profile.data, {
    revalidate: false,
  })

  const data: ISessionDetailed = {
    access_token: response.access_token,
    refresh_token: response.refresh_token,
    expires_in: response.expires_in,
    roles: response.roles,
    token_type: response.token_type,
    username: response.username,
    user: profile.data,
    impersonation: {
      originalUser: response.impersonated_by,
      startedAt: new Date().toISOString(),
    },
    ...computeSessionExpiration(response.expires_in),
  }

  functions.changeSession(data)

  return data
}

export function sweepExpiredSessions(value: IAppAuthState): IAppAuthState {
  const now = Date.now()

  const activeStillValid = value.active && !isSessionFullyExpired(value.active, now)
  const cleanedSessions = value.sessions.filter((s) => !isSessionFullyExpired(s, now))

  if (activeStillValid && cleanedSessions.length === value.sessions.length) {
    return value
  }

  return {
    ...value,
    active: activeStillValid ? value.active : undefined,
    sessions: cleanedSessions,
  }
}

//  //////////////////////////////////////////////
//  MARK: CONTEXT
export interface IAppAuthState {
  active?: ISessionDetailed

  sessions: ISessionDetailed[]
}

export interface IAppAuthStateStored extends IAppAuthState {}

export interface IAppAuthModel {}

export interface IAppAuthFunctions {
  login(username: string, password: string): Promise<ISessionDetailed>

  loginWithSsoToken(accessToken: string, keycloakRefreshToken?: string): Promise<ISessionDetailed>

  logout(username?: string): void

  changeSession(active?: ISessionDetailed): void

  updateSession(username: string, session: AxiosResponse<ISession>): void

  updateSessionData(username: string, session: Partial<ISessionDetailed>): void

  updateUser(username: string, user: AxiosResponse<IUser>): void

  updateGlobalEntity(username: string, globalEntity?: IEntitySimplified): void

  updateGlobalShareholder(username: string, globalShareholder?: IEntitySimplified): void

  postUpdatePassword(password: string): Promise<AxiosResponse<IUser>>

  requestPasswordReset(username: string): Promise<void>

  completePasswordReset(token: string, password: string): Promise<void>

  completeInvitation(token: string, password: string): Promise<ISessionDetailed>

  startImpersonation(targetUserId: number): Promise<ISessionDetailed>

  startImpersonationByUsername(username: string): Promise<ISessionDetailed>

  stopImpersonation(): Promise<void>

  setState: React.Dispatch<React.SetStateAction<IAppAuthState>>
}

interface IImpersonateResponse extends ISession {
  impersonated_by: IImpersonationOriginalUser
}

export interface IAppAuthContext extends IAppAuthState, IAppAuthModel, IAppAuthFunctions {}

export function getAppAuthContext(): IAppAuthModel {
  return {}
}

//  //////////////////////////////////////////////
//  MARK: CONTEXT FUNCTIONS
export function getAppAuthContextFunctions(
  state: IAppAuthState,
  setState: React.Dispatch<React.SetStateAction<IAppAuthState>>,
): IAppAuthFunctions {
  return {
    async login(username, password) {
      const session = await axios.post<ISession>('/api/login', {
        username,
        password,
      })

      const profile = await axios.get<IUser>('/api/user/profile', {
        headers: {
          Authorization: `${session.data.token_type} ${session.data.access_token}`,
        },
      })

      mutate(['/api/user/profile', profile.data.username], profile.data, { revalidate: false })
      mutate([`/api/user/${profile.data.id}`, profile.data.username], profile.data, {
        revalidate: false,
      })
      mutate([`/api/person/${profile.data.id}`, profile.data.username], profile.data, {
        revalidate: false,
      })

      const data: ISessionDetailed = {
        ...session.data,
        ...computeSessionExpiration(session.data.expires_in),
        user: profile.data,
      }

      this.changeSession(data)

      return data
    },

    async loginWithSsoToken(accessToken, keycloakRefreshToken) {
      // Le JWT vient du callback SSO Keycloak — pas de refresh_token
      const profile = await axios.get<IUser>('/api/user/profile', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      mutate(['/api/user/profile', profile.data.username], profile.data, { revalidate: false })
      mutate([`/api/user/${profile.data.id}`, profile.data.username], profile.data, {
        revalidate: false,
      })
      mutate([`/api/person/${profile.data.id}`, profile.data.username], profile.data, {
        revalidate: false,
      })

      const data: ISessionDetailed = {
        access_token: accessToken,
        refresh_token: '',
        expires_in: 3600,
        roles: (profile.data.roles ?? []).map((r) => r.authority),
        token_type: 'Bearer',
        username: profile.data.username,
        user: profile.data,
        // Stocker le refresh_token Keycloak pour renouveler la session sans re-login
        keycloak_refresh_token: keycloakRefreshToken,
        ...computeSessionExpiration(3600),
      }

      this.changeSession(data)

      return data
    },

    logout(username) {
      setState((oldState) => {
        if (!username) {
          username = oldState.active?.username
        }

        if (oldState.active?.username === username) {
          oldState.active = undefined
        }

        oldState.sessions = oldState.sessions.filter((e) => e.username !== username)

        return {
          ...oldState,
        }
      })
    },

    changeSession(active) {
      setState((oldState) => {
        if (oldState.active) {
          oldState.sessions = [
            oldState.active,
            ...oldState.sessions.filter((e) => e.username !== oldState.active?.username),
          ]
        }

        oldState.active = active

        if (oldState.active) {
          oldState.sessions = oldState.sessions.filter(
            (e) => e.username !== oldState.active?.username,
          )
        }

        return { ...oldState }
      })
    },

    updateSession(username, session) {
      setState((oldState) => {
        const expirations = computeSessionExpiration(session.data.expires_in)

        if (oldState.active?.username === username) {
          oldState.active = {
            ...oldState.active,
            ...session.data,
            ...expirations,
          }
        }

        oldState.sessions = oldState.sessions.map((e) =>
          e.username === username ? { ...e, ...session.data, ...expirations } : e,
        )

        return { ...oldState }
      })
    },

    updateSessionData(username, session) {
      setState((oldState) => {
        if (oldState.active?.username === username) {
          oldState.active = {
            ...oldState.active,
            ...session,
          }
        }

        oldState.sessions = oldState.sessions.map((e) =>
          e.username === username ? { ...e, ...session } : e,
        )

        return { ...oldState }
      })
    },

    updateUser(username, response) {
      setState((oldState) => {
        if (oldState.active?.username === username) {
          oldState.active = {
            ...oldState.active,
            user: response.data,
          }
        }

        oldState.sessions = oldState.sessions.map((e) =>
          e.username === username ? { ...e, user: response.data } : e,
        )

        mutate(['/api/user/profile', username], response, { revalidate: false })
        mutate([`/api/user/${response.data.id}`, username], response, {
          revalidate: false,
        })
        mutate([`/api/person/${response.data.id}`, username], response, {
          revalidate: false,
        })

        return { ...oldState }
      })
    },

    updateGlobalEntity(username, globalEntity) {
      this.updateSessionData(username, { globalEntity })
    },

    updateGlobalShareholder(username, globalShareholder) {
      this.updateSessionData(username, { globalShareholder })
    },

    async postUpdatePassword(password) {
      const response = await axios.post<IUser>('/api/user/updatePassword', { password })

      this.updateUser(response.data.username, response)

      return response
    },

    async requestPasswordReset(username) {
      await axios.get('/api/user/resetPassword', {
        params: { username },
      })
    },

    async completePasswordReset(token, password) {
      await axios.post('/api/user/resetPassword', { token, password })
    },

    async startImpersonation(targetUserId) {
      const session = await axios.post<IImpersonateResponse>(
        `/api/user/impersonate/${targetUserId}`,
      )

      return finalizeImpersonationLogin(session.data, this)
    },

    async startImpersonationByUsername(username) {
      const session = await axios.post<IImpersonateResponse>('/api/user/impersonate/byUsername', {
        username,
      })

      return finalizeImpersonationLogin(session.data, this)
    },

    async stopImpersonation() {
      try {
        await axios.post('/api/user/impersonate/stop')
      } catch {
        // Audit best-effort : on revient sur la session admin même si le stop
        // serveur a échoué (déconnexion, JWT déjà invalidé, etc.).
      }

      setState((oldState) => {
        const current = oldState.active

        if (!current?.impersonation) {
          return oldState
        }

        const originalUsername = current.impersonation.originalUser.username

        const original = oldState.sessions.find((s) => s.username === originalUsername)

        if (!original) {
          return {
            ...oldState,
            active: undefined,
            sessions: oldState.sessions.filter((s) => s.username !== current.username),
          }
        }

        return {
          ...oldState,
          active: original,
          sessions: oldState.sessions.filter(
            (s) => s.username !== originalUsername && s.username !== current.username,
          ),
        }
      })
    },

    async completeInvitation(token, password) {
      // /api/invitation/accept renvoie le format standard /api/login depuis
      // que le serveur émet aussi le refresh_token (cohérence iOS + web).
      const response = await axios.post<ISession>('/api/invitation/accept', { token, password })

      const profile = await axios.get<IUser>('/api/user/profile', {
        headers: {
          Authorization: `${response.data.token_type} ${response.data.access_token}`,
        },
      })

      mutate(['/api/user/profile', profile.data.username], profile.data, { revalidate: false })
      mutate([`/api/user/${profile.data.id}`, profile.data.username], profile.data, {
        revalidate: false,
      })
      mutate([`/api/person/${profile.data.id}`, profile.data.username], profile.data, {
        revalidate: false,
      })

      const data: ISessionDetailed = {
        ...response.data,
        ...computeSessionExpiration(response.data.expires_in),
        user: profile.data,
      }

      this.changeSession(data)

      return data
    },

    setState,
  }
}

//  //////////////////////////////////////////////
//  MARK: INITIAL STATE
export function getAppAuthInitialState(): IAppAuthState {
  return {
    active: undefined,
    sessions: [],
  }
}

export function parseAppAuthState(value: IAppAuthStateStored): IAppAuthState {
  return {
    ...getAppAuthInitialState(),
    ...value,
  }
}

export function validateAppAuthState(value: IAppAuthStateStored): IAppAuthState {
  return sweepExpiredSessions(value)
}

export function serializeAppAuthState(value: IAppAuthState): IAppAuthStateStored {
  return {
    active: value.active,
    sessions: value.sessions,
  }
}
